import { gql } from '@apollo/client';

export const GET_STUDIO_BOOKING_AVAILABILITY = gql`
  query GetStudioBookingAvailability($id: ID!) {
    getStudio(id: $id) {
      studioLocation
      city
      country
      locationLabel
      id
      userID
      pricePerHour
      studioSize
      numberOfPeople
      minNumberOfHours
      pricingMode
      tieredPricing {
        minPersons
        maxPersons
        pricePerHour
      }
      packages {
        id
        name
        durationHours
        prices {
          minPersons
          maxPersons
          price
        }
      }
      discountRules {
        minHours
        discountType
        discountValue
      }
      weekendSurcharge {
        saturdayEnabled
        saturdaySurchargeType
        saturdaySurchargeValue
        sundayEnabled
        sundaySurchargeType
        sundaySurchargeValue
      }
      extraFees {
        id
        name
        amount
      }
      equipment {
        items {
          id
          studioID
          category
          items {
            id
            name
            count
            description
            imageKey
          }
        }
      }
      availabilities {
        items {
          id
          dayOfWeek
          startTime
          endTime
        }
      }
      blockedTimes {
        items {
          id
          startDateTime
          endDateTime
          isFullDay
        }
      }
    }
  }
`;

export const GET_STUDIO_EXACT_LOCATION = gql`
  query GetStudioExactLocation($studioID: ID!, $bookingID: ID) {
    getStudioExactLocation(studioID: $studioID, bookingID: $bookingID) {
      studioID
      streetAndNumber
      adressLine2
      city
      postcode
      country
      latitude
      longitude
    }
  }
`;

export const BOOKING_SLOTS_BY_STUDIO = gql`
  query BookingSlotsByStudio(
    $studioID: ID!
    $startDateTime: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelBookingSlotFilterInput
    $limit: Int
    $nextToken: String
  ) {
    bookingSlotsByStudioIDAndStartDateTime(
      studioID: $studioID
      startDateTime: $startDateTime
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        startDateTime
        endDateTime
        slotStatus
      }
      nextToken
    }
  }
`;

export const GET_STUDIO_OWNER_BOOKINGS = gql`
  query GetStudioOwnerBookings($id: ID!) {
    getStudio(id: $id) {
      id
      userID
      bookings {
        items {
          id
          startDateTime
          endDateTime
          status
        }
      }
    }
  }
`;

export const CREATE_BOOKING = gql`
  mutation CreateBooking($input: CreateBookingInput!) {
    createBooking(input: $input) {
      id
      studioID
      studioOwnerID
      userID
      startDateTime
      endDateTime
      totalPrice
      status
      projectName
      companyName
      projectDescription
      totalStudioPrice
      totalCreativePrice
      creativePackages {
        creativeId
        packageId
        duration
        price
        specialRequests
      }
      paymentStatus
      paymentIntentID
      guestReviewed
      hostReviewed
      createdAt
      updatedAt
    }
  }
`;

// PHASE 1: Slot reservieren (Lock-First Pattern)
// Erstellt NUR eine temporäre Reservation, KEIN Booking!
export const CREATE_BOOKING_SECURE = gql`
  mutation CreateBookingSecure($input: CreateBookingSecureInput!) {
    createBookingSecure(input: $input) {
      slotID
      reservationToken
      clientSecret
      paymentIntentID
      expiresAt
      usesConnect
      stripeConnectAccountId
      payoutMode
      totalAmountCents
      originalAmountCents
      campaignDiscountAmountCents
      totalPrice
      originalTotalPrice
      campaignDiscountAmount
      campaignApplied
    }
  }
`;

// PHASE 3: Nach erfolgreicher Zahlung - Booking erstellen
export const CONFIRM_BOOKING_PAYMENT = gql`
  mutation ConfirmBookingPayment($input: ConfirmBookingPaymentInput!) {
    confirmBookingPayment(input: $input) {
      booking {
        id
        chatRoomID
        studioID
        studioOwnerID
        userID
        startDateTime
        endDateTime
        totalPrice
        totalStudioPrice
        totalCreativePrice
        status
        projectName
        companyName
        projectDescription
        packageId
        packageName
        packageDurationHours
        bookedEquipment {
          equipmentItemId
          name
          quantity
        }
        totalEquipmentPrice
        appliedDiscount
        appliedSurcharge
        appliedFees {
          name
          amount
        }
        creativePackages {
          packageID
          packageName
          packageType
          price
          specialRequests
        }
        paymentStatus
        paymentIntentID
        createdAt
        updatedAt
      }
      success
    }
  }
`;

export const BOOKINGS_BY_USER_ID = gql `query BookingsByUserID(
  $userID: ID!
  $sortDirection: ModelSortDirection
  $filter: ModelBookingFilterInput
  $limit: Int
  $nextToken: String
) {
  bookingsByUserID(
    userID: $userID
    sortDirection: $sortDirection
    filter: $filter
    limit: $limit
    nextToken: $nextToken
  ) {
    items {
      id
      chatRoomID
      studioID
      userID
      startDateTime
      endDateTime
      totalPrice
      status
      guestReviewed
      hostReviewed
      cancelledBy
      cancelledAt
      projectName
      companyName
      projectDescription
      Studio {
        equipment {
        items {
          id
          studioID
          category
          items {
            id
            name
            count
          }
          }
        }
        id
        StudioName
        images
        studioLocation
        pricePerHour
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
        cancellationPolicy
        houseRules {
          smokingAllowed
          alcoholAllowed
          foodAllowed
          equipmentChangesAllowed
          pyrotechnicsAllowed
          glitterConfettiAllowed
          adultContentAllowed
          fogMachineAllowed
          customRules
        }
        User {
          id
          firstName
          lastName
          profilePicture
        }
        createdAt
        updatedAt
        __typename
      }
      User {
        id
      }
      creativePackages {
        packageID
        packageName
        packageType
        price
        specialRequests
      }
      createdAt
      updatedAt
      __typename
    }
    nextToken
    __typename
  }
}
`;

export const BOOKINGS_FOR_USER_STUDIOS = gql`
  query BookingsForUserStudios(
    $userID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelStudioFilterInput
    $limit: Int
    $nextToken: String
  ) {
    studiosByUserID(
      userID: $userID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        StudioName
        images
        studioLocation
        city
        country
        locationLabel
        bookings {
          items {
            id
            chatRoomID
            studioID
            studioOwnerID
            userID
            startDateTime
            endDateTime
            totalPrice
            status
            guestReviewed
            hostReviewed
            projectName
            companyName
            projectDescription
            creativePackages {
              packageID
              packageName
              packageType
              price
              specialRequests
            }
            createdAt
            updatedAt
            User {
              id
              firstName
              lastName
              profilePicture
              __typename
            }
            __typename
          }
          nextToken
          __typename
        }
        __typename
      }
      nextToken
      __typename
    }
  }
`;

export const UPDATE_BOOKING = gql `mutation UpdateBooking(
  $input: UpdateBookingInput!
  $condition: ModelBookingConditionInput
) {
  updateBooking(input: $input, condition: $condition) {
    id
    studioID
    userID
    startDateTime
    endDateTime
    totalPrice
    status
    Studio {
      id
      StudioName
      images
      studioLocation
      pricePerHour
      image
      description
      userID
      studioTypes
      studioSize
      numberOfPeople
      city
      postcode
      cancellationPolicy
      houseRules {
        smokingAllowed
        alcoholAllowed
        foodAllowed
        equipmentChangesAllowed
        pyrotechnicsAllowed
        glitterConfettiAllowed
        adultContentAllowed
        fogMachineAllowed
        customRules
      }
      User {
        id
        firstName
        lastName
        profilePicture
        numberOfStudios
        __typename
      }
      country
      approxLatitude
      approxLongitude
      locationLabel
      locationPrecision
      minNumberOfHours
      availabilities {
        nextToken
        __typename
      }
      bookings {
        nextToken
        __typename
      }
      createdAt
      updatedAt
      __typename
    }
    User {
      id
      firstName
      lastName
      profilePicture
      numberOfStudios
      Studios {
        nextToken
        __typename
      }
      bookings {
        nextToken
        __typename
      }
      __typename
    }
    createdAt
    updatedAt
    __typename
  }
}
`

export const BOOKING_BY_ID = gql`
  query GetBooking($id: ID!) {
    getBooking(id: $id) {
      id
      chatRoomID
      studioID
      studioOwnerID
      userID
      startDateTime
      endDateTime
      totalPrice
      status
      projectName
      companyName
      projectDescription
      totalStudioPrice
      totalCreativePrice
      totalEquipmentPrice
      appliedDiscount
      appliedSurcharge
      appliedFees {
        name
        amount
      }
      packageId
      packageName
      packageDurationHours
      bookedEquipment {
        equipmentItemId
        name
        quantity
      }
      paymentStatus
      paymentIntentID
      guestReviewed
      hostReviewed
      cancelledBy
      cancelledAt
      cancellationReason
      creativePackages {
        packageID
        packageName
        packageType
        price
        specialRequests
      }
      Studio {
        id
        StudioName
        images
        paidEquipmentItems {
          items {
            id
            name
            imageKey
            price
            __typename
          }
          nextToken
          __typename
        }
        studioLocation
        pricePerHour
        discountRules {
          minHours
          discountType
          discountValue
        }
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
        cancellationPolicy
        houseRules {
          smokingAllowed
          alcoholAllowed
          foodAllowed
          equipmentChangesAllowed
          pyrotechnicsAllowed
          glitterConfettiAllowed
          adultContentAllowed
          fogMachineAllowed
          customRules
        }
        User {
          id
          firstName
          lastName
          profilePicture
          __typename
        }
        __typename
      }
      User {
        id
        firstName
        lastName
        profilePicture
        __typename
      }
      reviews {
        items {
          id
          rating
          content
          communicationRating
          reviewerId
          reviewType
          __typename
        }
        nextToken
        __typename
      }
      createdAt
      updatedAt
      __typename
    }
  }
`;
